import { List } from "immutable";
import PropTypes from "prop-types";
import React, { Fragment } from "react";

import FormButton from "../../../components/form/FormButton";
import FormCheckbox from "../../../components/form/FormCheckbox";
import FormTextField from "../../../components/form/FormTextField";
import ModalFormDropdown from "../../../components/form/ModalFormDropdown";
import ModalCheckboxGrid from "../../../components/modal/ModalCheckboxGrid";
import ModalField from "../../../components/modal/ModalField";
import ModalGroup from "../../../components/modal/ModalGroup";
import ModalRow from "../../../components/modal/ModalRow";
import SettingsOptionList from "../../../components/optionsList/SettingsOptionList";
import PropertyFormats, {
  PropertyFormatAutoCalculateTypes,
  PropertyFormatIdentifier
} from "../../../constants/propertyFormats";
import { PropTypeShapes } from "../../../types";
import preprocessorQuery from "../../../utils/preprocessorQuery";
import userInputValidators from "../../../utils/userInputValidators";

const formatOptions = () => {
  return PropertyFormats.map(t => ({
    label: t.name,
    value: t.identifier
  }));
};

const OPTIONS_VIEW_ID = "SETTINGS_PROPERTIES_DETAILS_OPTIONS_VIEW_ID";

const renderAutoCalculateGroup = (
  p,
  onToggleAutoCalculate,
  onAutoCalculateTypeChange,
  onToggleIndicateItemCompletion
) => {
  const typeOptions = PropertyFormatAutoCalculateTypes[p.format];
  return typeOptions ? (
    <Fragment>
      <ModalRow>
        <ModalField flex={2}>
          <FormCheckbox
            label="Calculate parent values from children"
            checkboxState={p.autoCalculate}
            onChange={() => onToggleAutoCalculate(p.id)}
            helpTooltip="Values for parents are calculated based on the values of their children (e.g. the cost to complete a task is equal to the sum of the cost of its sub-tasks)"
          />
        </ModalField>
      </ModalRow>
      {p.format === PropertyFormatIdentifier.boolean || p.format === PropertyFormatIdentifier.percentage ? (
        <ModalRow>
          <ModalField flex={2}>
            <FormCheckbox
              label="Indicate item completion"
              checkboxState={p.indicateItemCompletion}
              onChange={() => onToggleIndicateItemCompletion(p.id)}
              helpTooltip="Items will be considered completed and crossed out based on this field (ticked checkbox or 100% progress)"
            />
          </ModalField>
        </ModalRow>
      ) : null}
      {p.autoCalculate ? (
        <ModalRow>
          <ModalField
            flex={1}
            label="Calculation Mode:"
            helpTooltip="The calculation used to determine a parent's value from its children"
          />
          <ModalField flex={2}>
            <ModalFormDropdown
              options={typeOptions.map(opt => {
                return { label: opt, value: opt };
              })}
              value={p.autoCalculateType}
              onChange={type => onAutoCalculateTypeChange(p.id, type)}
            />
          </ModalField>
        </ModalRow>
      ) : null}
    </Fragment>
  ) : null;
};

const renderTopGroup = (
  p,
  onLabelChange,
  onFormatChange,
  onToggleAutoCalculate,
  onAutoCalculateTypeChange,
  onToggleIndicateItemCompletion,
  onEnabledChange
) => {
  return (
    <ModalGroup>
      <ModalRow>
        <ModalField label="Name">
          <FormTextField
            value={p.label}
            autoFocusOnEmpty={true}
            onChange={label => onLabelChange(p.id, label)}
            validator={userInputValidators.noEmpty}
          />
        </ModalField>
        <ModalField label="Format">
          <ModalFormDropdown
            options={formatOptions()}
            value={p.format}
            onChange={format => onFormatChange(p.id, format)}
          />
        </ModalField>
      </ModalRow>
      <ModalRow>
        <ModalField>
          <FormCheckbox checkboxState={p.enabled} label={"Enabled"} onChange={val => onEnabledChange(p.id, val)} />
        </ModalField>
      </ModalRow>
      {renderAutoCalculateGroup(p, onToggleAutoCalculate, onAutoCalculateTypeChange, onToggleIndicateItemCompletion)}
    </ModalGroup>
  );
};

const renderOptionsGroup = (p, onAddAllowedValue, onRenameAllowedValue, onDeleteAllowedValue, onMoveAllowedValue) => {
  if (p.format !== PropertyFormatIdentifier.list) {
    return null;
  }
  return (
    <ModalGroup>
      <ModalRow key="header">
        <ModalField label="Options" />
      </ModalRow>
      <SettingsOptionList
        items={p.allowed ? p.allowed : List()}
        showAdd={true}
        onRenameItem={(optionId, newValue) => onRenameAllowedValue(p.id, optionId, newValue)}
        onDeleteItem={optionId => onDeleteAllowedValue(p.id, optionId)}
        onAddItem={value => onAddAllowedValue(p.id, value)}
        viewId={OPTIONS_VIEW_ID}
        onMoveItemAtIndex={(oldIndex, newIndex) => onMoveAllowedValue(p.id, oldIndex, newIndex)}
      />
    </ModalGroup>
  );
};

const renderTypesList = (p, types, onIncludeInTypeChange) => {
  const isMobile = preprocessorQuery.isMobile();
  const checkboxes = types.allIds
    .map(tId => {
      const t = types.byId.get(tId);
      const isChecked = t.properties.indexOf(p.id) >= 0;
      return (
        <FormCheckbox
          key={t.id}
          label={t.label}
          checkboxState={isChecked}
          onChange={newState => onIncludeInTypeChange(t.id, p.id, newState)}
        />
      );
    })
    .toArray();

  return (
    <ModalGroup>
      <ModalRow>
        <ModalField
          flex={1}
          label={`Applies to Item Types`}
          helpTooltip="Items of the selected types will show this property. This mirrors the setting in the Types tab."
        >
          <ModalCheckboxGrid columns={isMobile ? 1 : 2}>{checkboxes}</ModalCheckboxGrid>
        </ModalField>
      </ModalRow>
    </ModalGroup>
  );
};

const SettingsPropertiesDetailView = props => {
  const isIos = preprocessorQuery.isIos();

  const p = props.selectedItem ? props.properties.byId.get(props.selectedItem) : null;
  if (!p) {
    return <div />;
  }

  return (
    <div>
      {renderTopGroup(
        p,
        props.onLabelChange,
        props.onFormatChange,
        props.onToggleAutoCalculate,
        props.onAutoCalculateTypeChange,
        props.onToggleIndicateItemCompletion,
        props.onEnabledChange
      )}
      {renderOptionsGroup(
        p,
        props.onAddAllowedValue,
        props.onRenameAllowedValue,
        props.onDeleteAllowedValue,
        props.onMoveAllowedValue
      )}
      {renderTypesList(p, props.types, props.onIncludeInTypeChange)}
      {isIos && props.canDelete ? (
        <ModalGroup>
          <ModalRow>
            <div className="settingsDetailDeleteButton">
              <FormButton destructive={true} label="Delete" onClick={props.onDeleteClick} />
            </div>
          </ModalRow>
        </ModalGroup>
      ) : null}
    </div>
  );
};

SettingsPropertiesDetailView.propTypes = {
  properties: PropTypeShapes.propertyDefinitionsIdList.isRequired,
  types: PropTypeShapes.denormalisedTypeDefinitionsIdList.isRequired,
  selectedItem: PropTypes.string,
  onFormatChange: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired,
  onToggleAutoCalculate: PropTypes.func.isRequired,
  onAutoCalculateTypeChange: PropTypes.func.isRequired,
  onToggleIndicateItemCompletion: PropTypes.func.isRequired,
  onIncludeInTypeChange: PropTypes.func.isRequired,
  onAddAllowedValue: PropTypes.func.isRequired,
  onRenameAllowedValue: PropTypes.func.isRequired,
  onDeleteAllowedValue: PropTypes.func.isRequired,
  onMoveAllowedValue: PropTypes.func.isRequired,
  canDelete: PropTypes.bool,
  onDeleteClick: PropTypes.func,
  onEnabledChange: PropTypes.func.isRequired
};

export default SettingsPropertiesDetailView;
